import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Shift, StoreSchedule, StoreException, ValidationError, Template } from '../types';
import { useStore } from './StoreContext';
import { useEmployees } from './EmployeeContext';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';
import { db } from '../firebase';
import { HistoryService } from '../services/historyService';
import { VacationRequest } from './VacationContext';
import { format, startOfWeek, endOfWeek, parseISO, isSameWeek } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  onSnapshot,
  query,
  orderBy,
  where
} from 'firebase/firestore';

interface ScheduleContextType {
  shifts: Shift[];
  storeSchedule: StoreSchedule[];
  storeExceptions: StoreException[];
  templates: Template[];
  addShift: (shift: Omit<Shift, 'id' | 'createdAt' | 'updatedAt'>) => Promise<ValidationError[]>;
  updateShift: (id: string, updates: Partial<Shift>) => Promise<ValidationError[]>;
  deleteShift: (id: string) => void;
  publishShifts: (shiftIds: string[]) => void;
  addStoreSchedule: (schedule: Omit<StoreSchedule, 'id'>) => void;
  updateStoreSchedule: (id: string, updates: Partial<StoreSchedule>) => void;
  addStoreException: (exception: Omit<StoreException, 'id'>) => void;
  updateStoreException: (id: string, updates: Partial<StoreException>) => void;
  deleteStoreException: (id: string) => void;
  saveTemplate: (name: string, shifts: Omit<Shift, 'id' | 'isPublished' | 'createdAt' | 'updatedAt'>[]) => void;
  applyTemplate: (templateId: string, startDate: string) => Promise<ValidationError[]>;
  copyWeekToNext: (startDate: string) => Promise<ValidationError[]>;
  checkVacationConflict: (employeeId: string, date: string) => Promise<boolean>;
  getShiftsByStore: (storeId: string) => Shift[];
  getAllShifts: () => Shift[];
  isLoading: boolean;
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined);

// Horario de tienda por defecto (Lunes a Domingo + Feriados)
const defaultStoreSchedule: StoreSchedule[] = [
  { 
    id: '1', 
    dayOfWeek: 1, 
    isOpen: true, 
    timeRanges: [{ id: 'range_1', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Lunes
  { 
    id: '2', 
    dayOfWeek: 2, 
    isOpen: true, 
    timeRanges: [{ id: 'range_2', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Martes
  { 
    id: '3', 
    dayOfWeek: 3, 
    isOpen: true, 
    timeRanges: [{ id: 'range_3', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Miércoles
  { 
    id: '4', 
    dayOfWeek: 4, 
    isOpen: true, 
    timeRanges: [{ id: 'range_4', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Jueves
  { 
    id: '5', 
    dayOfWeek: 5, 
    isOpen: true, 
    timeRanges: [{ id: 'range_5', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Viernes
  { 
    id: '6', 
    dayOfWeek: 6, 
    isOpen: true, 
    timeRanges: [{ id: 'range_6', openTime: '09:00', closeTime: '20:00' }],
    openTime: '09:00', 
    closeTime: '20:00' 
  }, // Sábado
  { 
    id: '7', 
    dayOfWeek: 0, 
    isOpen: false, 
    timeRanges: [] 
  }, // Domingo cerrado
  { 
    id: '8', 
    dayOfWeek: 7, 
    isOpen: false, 
    timeRanges: [] 
  }, // Feriados cerrado por defecto
];

// Función para migrar horarios antiguos a la nueva estructura
const migrateStoreSchedule = (schedule: StoreSchedule): StoreSchedule => {
  // Si ya tiene timeRanges, no hacer nada
  if (schedule.timeRanges && schedule.timeRanges.length > 0) {
    return schedule;
  }
  
  // Si tiene openTime y closeTime, migrar a timeRanges
  if (schedule.openTime && schedule.closeTime) {
    return {
      ...schedule,
      timeRanges: [{
        id: `range_${schedule.id}_${Date.now()}`,
        openTime: schedule.openTime,
        closeTime: schedule.closeTime
      }]
    };
  }
  
  // Si no tiene horarios, usar array vacío
  return {
    ...schedule,
    timeRanges: []
  };
};

// Función para calcular horas entre dos tiempos
const calculateHours = (startTime: string, endTime: string, breakDuration?: number): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  let totalHours = (endMinutes - startMinutes) / 60;
  
  // Restar el tiempo de descanso si existe (breakDuration está en minutos)
  if (breakDuration && breakDuration > 0) {
    totalHours -= (breakDuration / 60);
  }
  
  return totalHours;
};

// Función para convertir tiempo a minutos
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Función para validar que los minutos sean múltiplos de 5
const validateMinutesAreMultipleOf5 = (time: string): boolean => {
  const [, minutes] = time.split(':');
  if (!minutes) return false;
  const m = parseInt(minutes, 10);
  return m % 5 === 0;
};

// Función para detectar conflictos entre turnos del mismo empleado
const checkShiftConflict = (newShift: { employeeId: string; date: string; startTime: string; endTime: string }, existingShifts: Shift[], excludeId?: string): boolean => {
  const newStartMinutes = timeToMinutes(newShift.startTime);
  const newEndMinutes = timeToMinutes(newShift.endTime);
  
  return existingShifts.some(shift => {
    // Excluir el turno que se está editando
    if (excludeId && shift.id === excludeId) {
      return false;
    }
    
    // Solo verificar turnos del mismo empleado en la misma fecha
    if (shift.employeeId !== newShift.employeeId || shift.date !== newShift.date) {
      return false;
    }
    
    const existingStartMinutes = timeToMinutes(shift.startTime);
    const existingEndMinutes = timeToMinutes(shift.endTime);
    
    // Verificar si hay solapamiento
    // Un turno se solapa si:
    // - Su inicio está dentro del rango del otro turno, O
    // - Su fin está dentro del rango del otro turno, O
    // - Contiene completamente al otro turno
    return (newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes);
  });
};

export function ScheduleProvider({ children }: { children: ReactNode }) {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [allShifts, setAllShifts] = useState<Shift[]>([]);
  const [storeSchedule, setStoreSchedule] = useState<StoreSchedule[]>([]);
  const [storeExceptions, setStoreExceptions] = useState<StoreException[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [publishedShiftsState, setPublishedShiftsState] = useState<Map<string, Partial<Shift>>>(new Map());
  const { addNotification } = useNotifications();
  const { currentStore } = useStore();
  const { employees } = useEmployees();
  const { currentEmployee } = useAuth();

  // Cargar todos los turnos para estadísticas globales
  useEffect(() => {
    const shiftsRef = collection(db, 'shifts');
    const shiftsQuery = query(shiftsRef);

    const unsubscribe = onSnapshot(shiftsQuery, (snapshot) => {
      const allShiftsData: Shift[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        allShiftsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as Shift);
      });
      
      // Ordenar por fecha en JavaScript
      allShiftsData.sort((a, b) => a.date.localeCompare(b.date));
      
      setAllShifts(allShiftsData);
    }, (error) => {
      console.error('Error loading all shifts:', error);
    });

    return () => unsubscribe();
  }, []);

  // Cargar datos desde Firebase
  useEffect(() => {
    console.log('ScheduleContext: useEffect ejecutándose, currentStore:', currentStore?.id || 'ninguna');
    if (!currentStore) {
      console.log('ScheduleContext: No hay tienda seleccionada, limpiando datos');
      setShifts([]);
      setStoreSchedule([]);
      setStoreExceptions([]);
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Filtrar shifts de la tienda actual desde allShifts
        const storeShifts = allShifts.filter(shift => shift.storeId === currentStore.id);
        setShifts(storeShifts);
        
        // Inicializar el estado de turnos publicados para rastrear cambios
        setPublishedShiftsState(prev => {
          const newMap = new Map(prev);
          storeShifts.forEach(shift => {
            if (shift.isPublished) {
              newMap.set(shift.id, {
                employeeId: shift.employeeId,
                date: shift.date,
                startTime: shift.startTime,
                endTime: shift.endTime,
                hours: shift.hours
              });
            }
          });
          return newMap;
        });

        // Cargar store schedule
        const storeScheduleRef = collection(db, 'storeSchedule');
        const storeScheduleQuery = query(storeScheduleRef, orderBy('dayOfWeek'));
        const storeScheduleUnsubscribe = onSnapshot(storeScheduleQuery, async (snapshot) => {
          const scheduleData: StoreSchedule[] = [];
          snapshot.forEach((doc) => {
            scheduleData.push({ id: doc.id, ...doc.data() } as StoreSchedule);
          });
          
          console.log('ScheduleContext: Cargando storeSchedule desde Firebase:', scheduleData.length, 'documentos encontrados');
          console.log('ScheduleContext: IDs de storeSchedule:', scheduleData.map(s => s.id));
          console.log('ScheduleContext: isClearingData flag:', localStorage.getItem('isClearingData'));

          // COMENTADO: No crear storeSchedule automáticamente para evitar recreación durante borrado
          // Los horarios de tienda deben ser creados explícitamente por el usuario o el generador de datos
          /*
          // Verificar si faltan horarios por defecto y agregarlos
          // Solo si hay una tienda seleccionada y hay datos de schedule
          const missingDefaultDays = defaultStoreSchedule.filter(defaultDay =>
            !scheduleData.some(s => s.dayOfWeek === defaultDay.dayOfWeek)
          );

          // Solo agregar días faltantes si hay una tienda seleccionada
          // Esto evita la recreación automática durante el borrado de datos
          if (currentStore && missingDefaultDays.length > 0 && scheduleData.length > 0) {
            // Agregar días faltantes a Firebase
            for (const missingDay of missingDefaultDays) {
              try {
                await addDoc(collection(db, 'storeSchedule'), {
                  dayOfWeek: missingDay.dayOfWeek,
                  isOpen: missingDay.isOpen,
                  openTime: missingDay.openTime || null,
                  closeTime: missingDay.closeTime || null,
                });
              } catch (error) {
                console.error(`Error adding default store schedule for day ${missingDay.dayOfWeek}:`, error);
              }
            }
          }
          */

          console.log('ScheduleContext: Datos originales de Firebase:', scheduleData.length);
          console.log('ScheduleContext: Datos por dayOfWeek:', scheduleData.map(s => ({ id: s.id, dayOfWeek: s.dayOfWeek })));
          
          // Eliminar duplicados y migrar datos
          const uniqueSchedule = scheduleData.reduce((acc, schedule) => {
            const existing = acc.find(s => s.dayOfWeek === schedule.dayOfWeek);
            if (!existing) {
              acc.push(migrateStoreSchedule(schedule));
            } else {
              console.log('ScheduleContext: Duplicado encontrado para dayOfWeek:', schedule.dayOfWeek);
            }
            return acc;
          }, [] as StoreSchedule[]);
          
          console.log('ScheduleContext: Horarios únicos después de eliminar duplicados:', uniqueSchedule.length);
          console.log('ScheduleContext: Horarios únicos:', uniqueSchedule.map(s => ({ id: s.id, dayOfWeek: s.dayOfWeek, isOpen: s.isOpen })));

          // Ordenar: Lunes -> Domingo -> Feriados
          const sortedSchedule = uniqueSchedule.sort((a, b) => {
            const order = (day: number) => {
              if (day === 1) return 0; // Lunes
              if (day === 2) return 1; // Martes
              if (day === 3) return 2; // Miércoles
              if (day === 4) return 3; // Jueves
              if (day === 5) return 4; // Viernes
              if (day === 6) return 5; // Sábado
              if (day === 0) return 6; // Domingo
              if (day === 7) return 7; // Feriados
              return day;
            };
            return order(a.dayOfWeek) - order(b.dayOfWeek);
          });

          setStoreSchedule(sortedSchedule);
        });

        // Cargar store exceptions
        const exceptionsRef = collection(db, 'storeExceptions');
        const exceptionsQuery = query(exceptionsRef, orderBy('date'));
        const exceptionsUnsubscribe = onSnapshot(exceptionsQuery, (snapshot) => {
          const exceptionsData: StoreException[] = [];
          snapshot.forEach((doc) => {
            exceptionsData.push({ id: doc.id, ...doc.data() } as StoreException);
          });
          setStoreExceptions(exceptionsData);
        });

        // Cargar templates
        const templatesRef = collection(db, 'templates');
        const templatesQuery = query(templatesRef, orderBy('name'));
        const templatesUnsubscribe = onSnapshot(templatesQuery, (snapshot) => {
          const templatesData: Template[] = [];
          snapshot.forEach((doc) => {
            templatesData.push({ id: doc.id, ...doc.data() } as Template);
          });
          setTemplates(templatesData);
        });

        setIsLoading(false);

        // Cleanup function
        return () => {
          storeScheduleUnsubscribe();
          exceptionsUnsubscribe();
          templatesUnsubscribe();
        };
      } catch (error) {
        console.error('Error loading data:', error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentStore, allShifts]);

  // Inicializar datos por defecto si no existen
  useEffect(() => {
    const initializeDefaultData = async () => {
      try {
        // Verificar si ya existe store schedule
        const storeScheduleRef = collection(db, 'storeSchedule');
        const scheduleSnapshot = await getDocs(storeScheduleRef);
        
        // COMENTADO: No crear storeSchedule automáticamente para evitar recreación durante borrado
        // Los horarios se crearán cuando el usuario configure una tienda
        /*
        const isClearingData = localStorage.getItem('isClearingData') === 'true';
        if (scheduleSnapshot.empty && !isClearingData) {
          // Agregar horario por defecto
          const addPromises = defaultStoreSchedule.map(schedule => {
            const { id, ...scheduleData } = schedule;
            return addDoc(storeScheduleRef, scheduleData);
          });
          await Promise.all(addPromises);
        }
        */
      } catch (error) {
        console.error('Error initializing default data:', error);
      }
    };

    if (!isLoading) {
      initializeDefaultData();
    }
  }, [isLoading]);

  const checkVacationConflict = async (employeeId: string, date: string): Promise<boolean> => {
    try {
      const vacationRequestsRef = collection(db, 'vacationRequests');
      const q = query(
        vacationRequestsRef,
        where('employeeId', '==', employeeId),
        where('status', '==', 'approved')
      );
      
      const snapshot = await getDocs(q);
      
      for (const doc of snapshot.docs) {
        const vacation = doc.data() as VacationRequest;
        const vacationStart = new Date(vacation.startDate);
        const vacationEnd = new Date(vacation.endDate);
        const shiftDate = new Date(date);
        
        // Verificar si la fecha del turno está dentro del período de vacaciones
        if (shiftDate >= vacationStart && shiftDate <= vacationEnd) {
          return true; // Hay conflicto
        }
      }
      
      return false; // No hay conflicto
    } catch (error) {
      console.error('Error checking vacation conflict:', error);
      return false; // En caso de error, permitir el turno
    }
  };

  const getShiftsByStore = (storeId: string): Shift[] => {
    return allShifts.filter(shift => shift.storeId === storeId);
  };

  const getAllShifts = (): Shift[] => {
    return allShifts;
  };

  const addShift = async (shiftData: Omit<Shift, 'id' | 'createdAt' | 'updatedAt'>): Promise<ValidationError[]> => {
    const errors: ValidationError[] = [];
    
    console.log('ScheduleContext: addShift called with data:', shiftData);
    console.log('ScheduleContext: currentStore:', currentStore);
    
    // Validar que hay una tienda seleccionada
    if (!currentStore) {
      console.log('ScheduleContext: No currentStore, returning error');
      errors.push({ type: 'schedule', message: 'No hay tienda seleccionada' });
      return errors;
    }
    
    // Validar que los horarios no superen las 24 horas
    const startHour = parseInt(shiftData.startTime.split(':')[0]);
    const endHour = parseInt(shiftData.endTime.split(':')[0]);
    
    if (startHour < 0 || startHour > 23) {
      errors.push({ type: 'schedule', message: 'La hora de inicio debe estar entre 00:00 y 23:59' });
    }
    
    if (endHour < 0 || endHour > 23) {
      errors.push({ type: 'schedule', message: 'La hora de fin debe estar entre 00:00 y 23:59' });
    }
    
    // Validar que los minutos sean múltiplos de 5
    if (!validateMinutesAreMultipleOf5(shiftData.startTime)) {
      errors.push({ type: 'schedule', message: 'Los minutos de inicio deben ser múltiplos de 5 (00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)' });
    }
    
    if (!validateMinutesAreMultipleOf5(shiftData.endTime)) {
      errors.push({ type: 'schedule', message: 'Los minutos de fin deben ser múltiplos de 5 (00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)' });
    }
    
    // Validar que la hora de fin sea posterior a la de inicio
    if (startHour > endHour || (startHour === endHour && parseInt(shiftData.startTime.split(':')[1]) >= parseInt(shiftData.endTime.split(':')[1]))) {
      errors.push({ type: 'schedule', message: 'La hora de fin debe ser posterior a la hora de inicio' });
    }
    
    // Validar que el empleado no esté de vacaciones en esa fecha
    const hasVacationConflict = await checkVacationConflict(shiftData.employeeId, shiftData.date);
    if (hasVacationConflict) {
      errors.push({ type: 'schedule', message: 'No se puede asignar un turno durante las vacaciones aprobadas del empleado' });
    }
    
    // Validar que el empleado no tenga fecha de terminación
    const employee = employees.find(emp => emp.id === shiftData.employeeId);
    if (employee && employee.terminationDate && shiftData.date >= employee.terminationDate) {
      errors.push({ type: 'schedule', message: `No se puede asignar turnos a ${employee.name} desde la fecha de baja (${employee.terminationDate}) en adelante` });
    }
    
    // Validar que no haya conflictos con otros turnos del mismo empleado
    const hasShiftConflict = checkShiftConflict(shiftData, shifts);
    if (hasShiftConflict) {
      errors.push({ type: 'schedule', message: 'El empleado ya tiene un turno asignado en ese horario' });
    }
    
    if (errors.length > 0) {
      return errors;
    }
    
    try {
      // Calcular horas automáticamente (considerando descanso si existe)
      const hours = calculateHours(
        shiftData.startTime, 
        shiftData.endTime, 
        shiftData.breakDuration
      );
      
      // Construir el objeto del turno, excluyendo campos undefined
      const newShift: any = {
        employeeId: shiftData.employeeId,
        date: shiftData.date,
        startTime: shiftData.startTime,
        endTime: shiftData.endTime,
        hours,
        isPublished: shiftData.isPublished,
        storeId: currentStore.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Solo incluir breakDuration si tiene un valor definido
      if (shiftData.breakDuration !== undefined && shiftData.breakDuration !== null) {
        newShift.breakDuration = shiftData.breakDuration;
      }

      console.log('ScheduleContext: Adding shift to Firebase:', newShift);
      const docRef = await addDoc(collection(db, 'shifts'), newShift);
      console.log('ScheduleContext: Shift added with ID:', docRef.id);
      
      // NO registrar en historial aquí - solo cuando se publique
    } catch (error) {
      console.error('Error adding shift:', error);
      errors.push({ type: 'schedule', message: 'Error al crear el turno' });
    }
    
    console.log('ScheduleContext: addShift returning errors:', errors);
    return errors;
  };

  const updateShift = async (id: string, updates: Partial<Shift>): Promise<ValidationError[]> => {
    const errors: ValidationError[] = [];
    
    // Validar que los horarios no superen las 24 horas si se están actualizando
    if (updates.startTime) {
      const startHour = parseInt(updates.startTime.split(':')[0]);
      if (startHour < 0 || startHour > 23) {
        errors.push({ type: 'schedule', message: 'La hora de inicio debe estar entre 00:00 y 23:59' });
      }
      // Validar que los minutos sean múltiplos de 5
      if (!validateMinutesAreMultipleOf5(updates.startTime)) {
        errors.push({ type: 'schedule', message: 'Los minutos de inicio deben ser múltiplos de 5 (00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)' });
      }
    }
    
    if (updates.endTime) {
      const endHour = parseInt(updates.endTime.split(':')[0]);
      if (endHour < 0 || endHour > 23) {
        errors.push({ type: 'schedule', message: 'La hora de fin debe estar entre 00:00 y 23:59' });
      }
      // Validar que los minutos sean múltiplos de 5
      if (!validateMinutesAreMultipleOf5(updates.endTime)) {
        errors.push({ type: 'schedule', message: 'Los minutos de fin deben ser múltiplos de 5 (00, 05, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)' });
      }
    }
    
    // Validar que la hora de fin sea posterior a la de inicio si se actualizan ambos
    if (updates.startTime && updates.endTime) {
      const startHour = parseInt(updates.startTime.split(':')[0]);
      const endHour = parseInt(updates.endTime.split(':')[0]);
      const startMinute = parseInt(updates.startTime.split(':')[1]);
      const endMinute = parseInt(updates.endTime.split(':')[1]);
      
      if (startHour > endHour || (startHour === endHour && startMinute >= endMinute)) {
        errors.push({ type: 'schedule', message: 'La hora de fin debe ser posterior a la hora de inicio' });
      }
    }
    
    // Validar que el empleado no esté de vacaciones si se actualiza la fecha o el empleado
    if (updates.date || updates.employeeId) {
      const currentShift = shifts.find(s => s.id === id);
      if (currentShift) {
        const employeeId = updates.employeeId || currentShift.employeeId;
        const date = updates.date || currentShift.date;
        
        const hasVacationConflict = await checkVacationConflict(employeeId, date);
        if (hasVacationConflict) {
          errors.push({ type: 'schedule', message: 'No se puede asignar un turno durante las vacaciones aprobadas del empleado' });
        }
      }
    }
    
    // Validar conflictos de turnos si se actualiza horario, empleado o fecha
    if (updates.startTime || updates.endTime || updates.employeeId || updates.date) {
      const currentShift = shifts.find(s => s.id === id);
      if (currentShift) {
        const updatedShift = {
          employeeId: updates.employeeId || currentShift.employeeId,
          date: updates.date || currentShift.date,
          startTime: updates.startTime || currentShift.startTime,
          endTime: updates.endTime || currentShift.endTime
        };
        
        const hasShiftConflict = checkShiftConflict(updatedShift, shifts, id);
        if (hasShiftConflict) {
          errors.push({ type: 'schedule', message: 'El empleado ya tiene un turno asignado en ese horario' });
        }
      }
    }
    
    if (errors.length > 0) {
      return errors;
    }
    
    try {
      const shiftRef = doc(db, 'shifts', id);
      
      const currentShift = shifts.find(s => s.id === id);
      if (!currentShift) {
        errors.push({ type: 'schedule', message: 'Turno no encontrado' });
        return errors;
      }

      // Si se actualiza startTime, endTime o descanso, recalcular hours
      if (updates.startTime || updates.endTime || updates.breakDuration !== undefined) {
        const startTime = updates.startTime || currentShift.startTime;
        const endTime = updates.endTime || currentShift.endTime;
        const breakDuration = updates.breakDuration !== undefined ? updates.breakDuration : currentShift.breakDuration;
        updates.hours = calculateHours(startTime, endTime, breakDuration);
      }

      // Preparar cambios para el historial
      const changes: Record<string, { old: any; new: any }> = {};
      Object.keys(updates).forEach(key => {
        if (key !== 'updatedAt' && key !== 'createdAt' && key !== 'id' && key !== 'hours') {
          changes[key] = {
            old: currentShift[key as keyof Shift],
            new: updates[key as keyof Shift]
          };
        }
      });

      // Filtrar valores undefined antes de actualizar
      const updateData: any = {
        isPublished: false, // Marcar como no publicado cuando se modifica
        updatedAt: new Date().toISOString()
      };
      
      // Solo incluir campos que tienen valores definidos
      Object.keys(updates).forEach(key => {
        const value = updates[key as keyof Shift];
        if (value !== undefined && value !== null) {
          updateData[key] = value;
        }
      });
      
      console.log('ScheduleContext: Updating shift with data:', updateData);
      await updateDoc(shiftRef, updateData);
      console.log('ScheduleContext: Shift updated successfully');
      
      // NO registrar en historial aquí - solo cuando se publique
    } catch (error) {
      console.error('Error updating shift:', error);
      errors.push({ type: 'schedule', message: 'Error al actualizar el turno' });
    }
    
    return errors;
  };

  const deleteShift = async (id: string) => {
    try {
      const shift = shifts.find(s => s.id === id);
      await deleteDoc(doc(db, 'shifts', id));
      
      // Registrar en historial solo si el turno estaba publicado
      if (currentEmployee && shift && shift.isPublished) {
        const employee = employees.find(emp => emp.id === shift.employeeId);
        await HistoryService.logShiftDeleted(id, currentEmployee.id, employee?.name);
      }
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };

  const publishShifts = async (shiftIds: string[]) => {
    try {
      if (!currentEmployee) return;

      // Primero, actualizar todos los turnos y recopilar información
      const shiftsToProcess: Array<{
        shift: Shift;
        wasPublished: boolean;
        lastPublishedState: any;
        employee: any;
      }> = [];

      const updatePromises = shiftIds.map(async (id) => {
        const shiftRef = doc(db, 'shifts', id);
        const shift = shifts.find(s => s.id === id);
        if (!shift) return;
        
        const wasPublished = shift.isPublished || false;
        const lastPublishedState = publishedShiftsState.get(id);
        const employee = employees.find(emp => emp.id === shift.employeeId);
        
        await updateDoc(shiftRef, { 
          isPublished: true,
          updatedAt: new Date()
        });
        
        // Guardar el estado actual como el último estado publicado
        setPublishedShiftsState(prev => {
          const newMap = new Map(prev);
          newMap.set(id, {
            employeeId: shift.employeeId,
            date: shift.date,
            startTime: shift.startTime,
            endTime: shift.endTime,
            hours: shift.hours
          });
          return newMap;
        });

        shiftsToProcess.push({
          shift,
          wasPublished,
          lastPublishedState,
          employee
        });
      });
      
      await Promise.all(updatePromises);

      // Agrupar turnos nuevos por empleado y por semana
      const newShiftsByEmployeeAndWeek = new Map<string, Map<string, Shift[]>>();
      const modifiedShifts: Array<{
        shift: Shift;
        changes: Record<string, { old: any; new: any }>;
        employee: any;
      }> = [];

      for (const { shift, wasPublished, lastPublishedState, employee } of shiftsToProcess) {
        if (!wasPublished) {
          // Turno nuevo
          await HistoryService.logShiftCreated(shift.id, currentEmployee.id, employee?.name);
          
          const weekStart = startOfWeek(parseISO(shift.date), { weekStartsOn: 1 });
          const weekKey = format(weekStart, 'yyyy-MM-dd');
          
          if (!newShiftsByEmployeeAndWeek.has(shift.employeeId)) {
            newShiftsByEmployeeAndWeek.set(shift.employeeId, new Map());
          }
          const employeeWeeks = newShiftsByEmployeeAndWeek.get(shift.employeeId)!;
          if (!employeeWeeks.has(weekKey)) {
            employeeWeeks.set(weekKey, []);
          }
          employeeWeeks.get(weekKey)!.push(shift);
        } else if (lastPublishedState) {
          // Turno modificado
          const changes: Record<string, { old: any; new: any }> = {};
          if (lastPublishedState.date !== shift.date) {
            changes.date = { old: lastPublishedState.date, new: shift.date };
          }
          if (lastPublishedState.startTime !== shift.startTime) {
            changes.startTime = { old: lastPublishedState.startTime, new: shift.startTime };
          }
          if (lastPublishedState.endTime !== shift.endTime) {
            changes.endTime = { old: lastPublishedState.endTime, new: shift.endTime };
          }
          
          if (Object.keys(changes).length > 0) {
            await HistoryService.logShiftUpdated(shift.id, currentEmployee.id, changes, employee?.name);
            modifiedShifts.push({ shift, changes, employee });
          }
        }
      }

      // Crear notificaciones agrupadas para turnos nuevos
      for (const [employeeId, weeks] of Array.from(newShiftsByEmployeeAndWeek.entries())) {
        for (const [weekKey, weekShifts] of Array.from(weeks.entries())) {
          if (weekShifts.length === 0) continue;

          const weekStart = parseISO(weekKey);
          const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
          
          if (weekShifts.length === 1) {
            // Un solo turno - notificación individual
            const shift = weekShifts[0];
            const shiftDate = parseISO(shift.date);
            try {
              await addNotification({
                userId: employeeId,
                type: 'schedule_new',
                title: 'Nuevo Horario Asignado',
                message: `Se te ha asignado un nuevo turno el ${format(shiftDate, 'd MMMM', { locale: es })} de ${shift.startTime} a ${shift.endTime}.`,
                data: {
                  shiftId: shift.id,
                  date: shift.date,
                  startTime: shift.startTime,
                  endTime: shift.endTime
                }
              });
            } catch (error) {
              console.error('Error creating notification for new shift:', error);
            }
          } else {
            // Múltiples turnos - notificación agrupada
            try {
              await addNotification({
                userId: employeeId,
                type: 'schedule_new',
                title: 'Nuevos Horarios Asignados',
                message: `Se te han publicado los horarios de la semana del ${format(weekStart, 'd MMMM', { locale: es })} al ${format(weekEnd, 'd MMMM', { locale: es })}.`,
                data: {
                  shiftIds: weekShifts.map((s: Shift) => s.id),
                  weekStart: weekKey,
                  weekEnd: format(weekEnd, 'yyyy-MM-dd'),
                  shiftCount: weekShifts.length
                }
              });
            } catch (error) {
              console.error('Error creating grouped notification for new shifts:', error);
            }
          }
        }
      }

      // Crear notificaciones mejoradas para turnos modificados
      for (const { shift, changes, employee } of modifiedShifts) {
        try {
          const shiftDate = parseISO(shift.date);
          const changeMessages: string[] = [];
          
          if (changes.date) {
            const oldDate = parseISO(changes.date.old);
            const newDate = parseISO(changes.date.new);
            changeMessages.push(`fecha de ${format(oldDate, 'd MMMM', { locale: es })} a ${format(newDate, 'd MMMM', { locale: es })}`);
          }
          if (changes.startTime || changes.endTime) {
            const oldStart = changes.startTime?.old || shift.startTime;
            const oldEnd = changes.endTime?.old || shift.endTime;
            const oldTime = `${oldStart} - ${oldEnd}`;
            const newTime = `${shift.startTime} - ${shift.endTime}`;
            changeMessages.push(`horario de ${oldTime} a ${newTime}`);
          }
          
          const changeMessage = changeMessages.length > 0 
            ? `Se ha modificado ${changeMessages.join(' y ')}.`
            : 'Se ha modificado.';
          
          await addNotification({
            userId: shift.employeeId,
            type: 'schedule_change',
            title: 'Horario Modificado',
            message: `Tu turno del ${format(shiftDate, 'd MMMM', { locale: es })} ha sido modificado. ${changeMessage}`,
            data: {
              shiftId: shift.id,
              date: shift.date,
              startTime: shift.startTime,
              endTime: shift.endTime,
              changes
            }
          });
        } catch (error) {
          console.error('Error creating notification for shift change:', error);
        }
      }
    } catch (error) {
      console.error('Error publishing shifts:', error);
    }
  };

  const addStoreSchedule = async (scheduleData: Omit<StoreSchedule, 'id'>) => {
    try {
      console.log('ScheduleContext: addStoreSchedule called with:', scheduleData);
      const docRef = await addDoc(collection(db, 'storeSchedule'), scheduleData);
      console.log('ScheduleContext: addStoreSchedule completed with ID:', docRef.id);
    } catch (error) {
      console.error('Error adding store schedule:', error);
    }
  };

  const updateStoreSchedule = async (id: string, updates: Partial<StoreSchedule>) => {
    // Registrar en historial antes de actualizar
    if (currentEmployee && currentStore) {
      await HistoryService.logStoreScheduleUpdated(currentStore.id, currentEmployee.id, currentStore.name);
    }
    try {
      console.log('ScheduleContext: updateStoreSchedule called with:', { id, updates });
      await updateDoc(doc(db, 'storeSchedule', id), updates);
      console.log('ScheduleContext: updateStoreSchedule completed successfully');
    } catch (error) {
      console.error('Error updating store schedule:', error);
    }
  };

  const addStoreException = async (exceptionData: Omit<StoreException, 'id'>) => {
    try {
      await addDoc(collection(db, 'storeExceptions'), exceptionData);
    } catch (error) {
      console.error('Error adding store exception:', error);
    }
  };

  const updateStoreException = async (id: string, updates: Partial<StoreException>) => {
    try {
      await updateDoc(doc(db, 'storeExceptions', id), updates);
    } catch (error) {
      console.error('Error updating store exception:', error);
    }
  };

  const deleteStoreException = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'storeExceptions', id));
    } catch (error) {
      console.error('Error deleting store exception:', error);
    }
  };

  const saveTemplate = async (name: string, templateShifts: Omit<Shift, 'id' | 'isPublished' | 'createdAt' | 'updatedAt'>[]) => {
    try {
      const templateData = {
        name,
        shifts: templateShifts,
        createdAt: new Date()
      };
      
      await addDoc(collection(db, 'templates'), templateData);
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const applyTemplate = async (templateId: string, startDate: string): Promise<ValidationError[]> => {
    const errors: ValidationError[] = [];
    
    // Validar que hay una tienda seleccionada
    if (!currentStore) {
      errors.push({ type: 'schedule', message: 'No hay tienda seleccionada' });
      return errors;
    }
    
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        errors.push({ type: 'schedule', message: 'Plantilla no encontrada' });
        return errors;
      }

      const addPromises = template.shifts.map(shift => {
        const newShift = {
          ...shift,
          storeId: currentStore.id,
          date: startDate,
          isPublished: false,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        return addDoc(collection(db, 'shifts'), newShift);
      });
      
      await Promise.all(addPromises);
    } catch (error) {
      console.error('Error applying template:', error);
      errors.push({ type: 'schedule', message: 'Error al aplicar la plantilla' });
    }
    
    return errors;
  };

  const copyWeekToNext = async (startDate: string): Promise<ValidationError[]> => {
    const errors: ValidationError[] = [];
    
    // Validar que hay una tienda seleccionada
    if (!currentStore) {
      errors.push({ type: 'schedule', message: 'No hay tienda seleccionada' });
      return errors;
    }
    
    try {
      // Calcular la fecha de inicio de la siguiente semana
      const currentDate = new Date(startDate);
      const nextWeekDate = new Date(currentDate);
      nextWeekDate.setDate(currentDate.getDate() + 7);
      const nextWeekStart = nextWeekDate.toISOString().split('T')[0];
      
      // Obtener turnos de la semana actual
      const weekShifts = shifts.filter(shift => shift.date === startDate);
      
      const addPromises = weekShifts.map(shift => {
        const newShift = {
          employeeId: shift.employeeId,
          storeId: currentStore.id,
          date: nextWeekStart,
          startTime: shift.startTime,
          endTime: shift.endTime,
          hours: shift.hours,
          isPublished: false,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        return addDoc(collection(db, 'shifts'), newShift);
      });
      
      await Promise.all(addPromises);
    } catch (error) {
      console.error('Error copying week:', error);
      errors.push({ type: 'schedule', message: 'Error al copiar la semana' });
    }
    
    return errors;
  };

  return (
    <ScheduleContext.Provider value={{
      shifts,
      storeSchedule,
      storeExceptions,
      templates,
      addShift,
      updateShift,
      deleteShift,
      publishShifts,
      addStoreSchedule,
      updateStoreSchedule,
      addStoreException,
      updateStoreException,
      deleteStoreException,
      saveTemplate,
      applyTemplate,
      copyWeekToNext,
      checkVacationConflict,
      getShiftsByStore,
      getAllShifts,
      isLoading
    }}>
      {children}
    </ScheduleContext.Provider>
  );
}

export function useSchedule() {
  const context = useContext(ScheduleContext);
  if (context === undefined) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
}